import { AvgCostConversion, MinTaxConversion } from '@interfaces/models/spec-id-fallback.enum';
import {
  SettlementFundAoAEnum,
  SettlementFundMap,
} from '@interfaces/settlement-fund/settlement-fund.enum';
import { formatBalance } from '@shared/helpers/selected-accounts';
import {
  AccountTypeNames,
  AccountTypes,
  CurrentInputAccount,
  Eligible_Divorce_Account_Types,
  Eligible_Joint_Account_Types_Pattern,
  Eligible_Trust_Account_Types,
  IReceipt,
  ISelectedAccount,
  ProductType,
  TransactionTypesEnum,
} from '@shared/models';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import {
  InternalTransferState,
  InternalTransferTypeEnum,
} from '@store/reducers/internal-transfer/internal-transfer.reducer';
import { IOpenAccountState } from '@store/reducers/open-account/open-account.reducer';

import { toSentenceLowerCase } from '../cashier-nonretirement';

const accountTypesMap = new Map<string, string>([
  [AccountTypes.ESTATE, AccountTypeNames.ESTATE],
  [AccountTypes.EXECUTOR_ESTATE, AccountTypeNames.EXECUTOR_ESTATE],
  [AccountTypes.INDIVIDUAL, AccountTypeNames.INDV],
  [AccountTypes.INDV, AccountTypeNames.INDV],
  [AccountTypes.IRA, AccountTypeNames.IRA],
  [AccountTypes.ORG, AccountTypeNames.ORG],
  [AccountTypes.ENDOWMENT, AccountTypeNames.ENDOWMENT],
  [AccountTypes.HOSPITAL, AccountTypeNames.HOSPITAL],
  [AccountTypes.SCHOOL_DISTRICT, AccountTypeNames.SCHOOL_DISTRICT],
  [AccountTypes.PARTNERSHIP, AccountTypeNames.PARTNERSHIP],
  [AccountTypes.CORPORATION, AccountTypeNames.CORPORATION],
  [AccountTypes.CORPORATION_BANKRUPTCY, AccountTypeNames.CORPORATION_BANKRUPTCY],
  [AccountTypes.IIG_CORPORATION, AccountTypeNames.IIG_CORPORATION],
  [
    AccountTypes.EXEMPT_UNINCORPORATED_ORGANIZATION,
    AccountTypeNames.EXEMPT_UNINCORPORATED_ORGANIZATION,
  ],
  [
    AccountTypes.NONEXEMPT_UNINCORPORATED_ORGANIZATION,
    AccountTypeNames.NONEXEMPT_UNINCORPORATED_ORGANIZATION,
  ],
  [AccountTypes.FOUNDATION, AccountTypeNames.FOUNDATION],
  [AccountTypes.SOLE_PROPRIETORSHIP, AccountTypeNames.SOLE_PROPRIETORSHIP],
  [AccountTypes.PROFESSIONAL_ASSOCIATION, AccountTypeNames.PROFESSIONAL_ASSOCIATION],
  [AccountTypes.PROFESSIONAL_CORPORATION, AccountTypeNames.PROFESSIONAL_CORPORATION],
  [
    AccountTypes.PROFESSIONAL_CORPORATION_ASSOCIATION,
    AccountTypeNames.PROFESSIONAL_CORPORATION_ASSOCIATION,
  ],
  [AccountTypes.TRUST, AccountTypeNames.TRUST],
  [AccountTypes.OTHER, AccountTypeNames.OTHER],
]);

export const getAccountTypeName = (accountType: string | undefined): string => {
  if (!accountType) {
    return AccountTypeNames.OTHER;
  }
  if (Eligible_Joint_Account_Types_Pattern.test(accountType)) {
    return AccountTypeNames.JOINT;
  } else if ([...Eligible_Trust_Account_Types].map((x) => x.toString()).includes(accountType)) {
    return AccountTypeNames.TRUST;
  } else if ([...Eligible_Divorce_Account_Types].map((x) => x.toString()).includes(accountType)) {
    return AccountTypeNames.IRA;
  }
  return accountTypesMap.get(accountType) ?? AccountTypeNames.OTHER;
};

export const formatProductTypeName = (productType: ProductType | null) => {
  let formattedProductTypeName = '';
  if (productType) {
    formattedProductTypeName = productType
      .split('_')
      .map((word: string) => toSentenceLowerCase(word))
      .join(' ');
  }

  return formattedProductTypeName;
};

export const getAccountAndProductString = (
  isCashPlus: boolean,
  account: ISelectedAccount | null,
): string => {
  if (!account || !account.accountType) {
    return 'undefined account';
  }
  const accountTypeName = getAccountTypeName(account.accountType);
  const productTypeName = ![
    AccountTypes.ESTATE.toString(),
    AccountTypes.EXECUTOR_ESTATE.toString(),
  ].includes(account.accountType)
    ? ' ' +
      formatProductTypeName(isCashPlus ? ProductType.CASH_PLUS : account.accountData.productType)
    : '';
  return `${accountTypeName}${productTypeName} account`;
};

export function getTransferTypeAndMethodString(
  transferFrom: InternalTransferState['transferFrom'],
): string {
  return transferFrom.transferType === InternalTransferTypeEnum.ENTIRE_ACCOUNT
    ? toSentenceLowerCase(transferFrom.transferType)
    : toSentenceLowerCase(transferFrom.transferType) +
        ' in ' +
        toSentenceLowerCase(transferFrom.transferMethod).toLowerCase();
}

export function buildAccountFromInternalTransfer(
  state: InternalTransferState,
  editable: boolean,
  isDivorceFlow: boolean = false,
  hasAvgCostHoldings?: boolean,
): IReceipt {
  const transferFrom: InternalTransferState['transferFrom'] = state.transferFrom;
  const isCashPlus: boolean = !!transferFrom.selectedAccount?.accountData?.cashManagementAccount;
  const editRoute = isDivorceFlow
    ? 'secure/divorce/transfer-details'
    : 'secure/internal-transfer/transfer-details';
  const receipt: IReceipt = {
    header: 'Transferring from',
    editable,
    editRoute,
    sectionEvidenceID: 'accountFrom',
    rows: [
      {
        label: 'Account type',
        values: [getAccountAndProductString(isCashPlus, transferFrom.selectedAccount)],
        evidenceID: 'fromAccountType',
      },
      {
        label: 'Account number',
        values: [`${transferFrom.selectedAccount?.accountNumber}`],
        evidenceID: 'fromAccountNumber',
      },
      {
        label: 'Transfer method',
        values: [getTransferTypeAndMethodString(transferFrom)],
        evidenceID: 'transferMethod',
      },
    ],
  };

  if (state.transferFrom.avgCostConversion !== AvgCostConversion.EMPTY && !isDivorceFlow) {
    receipt.rows.push({
      label: 'AvgCost to FIFO',
      values: [state.transferFrom.avgCostConversion === AvgCostConversion.FIFO ? 'Yes' : 'No'],
      evidenceID: 'avgCostConversion',
    });
  }

  if (state.transferFrom.minTaxConversion !== MinTaxConversion.EMPTY && !isDivorceFlow) {
    receipt.rows.push({
      label: 'MinTax to FIFO',
      values: [state.transferFrom.minTaxConversion === MinTaxConversion.FIFO ? 'Yes' : 'No'],
      evidenceID: 'minTaxConversion',
    });
  }

  if (!editable && hasAvgCostHoldings && !isDivorceFlow) {
    receipt.rows.push({
      label: 'Average cost change',
      values: [`${transferFrom.convertAvgCostHoldings ? 'Yes' : 'No'}`],
      evidenceID: 'hasAvgCostHoldings',
    });
  }

  if (state.transferFrom.convertSpecIDHoldings && !isDivorceFlow) {
    receipt.rows.push({
      label: 'Convert Spec ID holdings',
      values: ['Yes'],
      evidenceID: 'convertSpecIDHoldings',
    });
  }

  if (!!transferFrom.selectedAccount?.accountData.nickname) {
    receipt.rows.splice(3, 0, {
      label: 'Account nickname',
      values: [transferFrom.selectedAccount.accountData.nickname],
      evidenceID: 'accountNickname',
    });
  }

  receipt.rows.push({
    label: 'Est. total transfer value',
    values: [formatBalance(transferFrom.totalTransferValue)],
    evidenceID: 'totalTransferValue',
    isLabelBold: true,
  });

  return receipt;
}

// This assertion function enables us to discriminate the CurrentInputAccount union interface which
// helps us define whether we are transferring to a new or existing account
export function assertsIsCurrentAccount(
  account: CurrentInputAccount | null,
): account is ISelectedAccount {
  if (account === null) return false;
  return 'accountData' in account;
}

export function buildAccountToInternalTransfer(
  state: InternalTransferState,
  openAccount: IOpenAccountState,
  editable: boolean,
  settlementFund: SettlementFundAoAEnum | null,
  isDivorceFlow: boolean = false,
): IReceipt {
  const triage: InternalTransferState['triage'] = state.triage;
  const transferTo: InternalTransferState['transferTo'] = state.transferTo;
  const toAccountNumber = transferTo.selectedAccount?.accountNumber;
  // existing
  const isOwner = triage.isOwner;
  const isNewAccount: boolean =
    state.transactionType === TransactionTypesEnum.NEW_ACCOUNT ||
    state.transactionType === TransactionTypesEnum.FORCED_NEW_ACCOUNT;
  let isCashPlus: boolean = false;
  if (!isNewAccount) {
    // check if the account is not new and the user is the owner with a Cash Management Account.
    isCashPlus =
      isOwner &&
      !!(transferTo.selectedAccount as ISelectedAccount)?.accountData?.cashManagementAccount;
  }
  const editRoute = isDivorceFlow
    ? 'secure/divorce/transfer-details'
    : 'secure/internal-transfer/transfer-details';

  const receipt: IReceipt = {
    header: 'Transferring to',
    editable: editable,
    editRoute: editRoute,
    sectionEvidenceID: 'accountTo',
    rows: [
      {
        label: 'Account type',
        values: [
          isSelectedAccountWithData(transferTo.selectedAccount)
            ? `${getAccountTypeName(transferTo.selectedAccount.accountType)} ${formatProductTypeName(isCashPlus ? ProductType.CASH_PLUS : transferTo.selectedAccount.accountData.productType)} account`
            : `${getAccountTypeName(triage.transferTo)} account`,
        ],
        evidenceID: 'toAccountType',
      },
      {
        label: 'Account number',
        values: [
          (toAccountNumber === 'newAccount' ||
            !transferTo.selectedAccount?.accountNumber ||
            openAccount.openAccountResponse?.isS24Reject) &&
          !isDivorceFlow
            ? 'Account number pending'
            : transferTo.selectedAccount?.accountNumber,
        ],
        evidenceID: 'toAccountNumber',
      },
    ],
  };

  if (!assertsIsCurrentAccount(transferTo.selectedAccount) && !isDivorceFlow) {
    const toAccountName = transferTo.selectedAccount?.accountName;
    const accountName = {
      label: 'Name of individual or trust',
      values: [toAccountName],
      evidenceID: 'toAccountName',
    };
    receipt.rows.splice(1, 0, accountName);
  }

  if (
    (state.transactionType === TransactionTypesEnum.NEW_ACCOUNT ||
      state.transactionType === TransactionTypesEnum.FORCED_NEW_ACCOUNT) &&
    !isDivorceFlow
  ) {
    const settlementFundLabel = {
      label: 'Settlement fund',
      values: [SettlementFundMap[settlementFund ?? SettlementFundAoAEnum.FED]].filter(
        (value): value is string => value !== undefined,
      ),
      evidenceID: 'settlementFund',
      additionalText: ReviewContent.settlementFundDescription,
    };
    receipt.rows.splice(2, 0, settlementFundLabel);
  }

  if (
    isSelectedAccountWithData(transferTo.selectedAccount) &&
    transferTo.selectedAccount.accountData.nickname
  ) {
    receipt.rows.push({
      label: 'Account nickname',
      values: [transferTo.selectedAccount.accountData.nickname],
      evidenceID: 'accountNickname',
    });
  }

  return receipt;
}

export const isSelectedAccountWithData = (
  account: CurrentInputAccount,
): account is ISelectedAccount => {
  const selectedAccount = account as ISelectedAccount;
  return !!(
    selectedAccount?.accountType &&
    selectedAccount?.accountData &&
    Object.keys(selectedAccount.accountData).length
  );
};
